/* storybook-check-ignore */
import NextImage from 'next/image';
import { Box, Flex, Text, Link } from '@opendoor/bricks/core';
import { Avatar } from '@opendoor/bricks/complex';
import Icon from '@opendoor/bricks/core/Icon/Icon';
import SelectiveSpritesheet from '@opendoor/bricks/core/Icon/SelectiveSpritesheet';
import NewTab from '@opendoor/bricks/core/Icon/SpritesheetIcons/NewTab';
import { imgixLoader } from '../../helpers/imgixUrl';
import { IAuthor, IEditor, IReviewer } from '../../declarations/contentful';
import typography from './typography';
import { getSocialLink } from '../../helpers/socialLinks';

// Person component, with a place for extra text (i.e. passed in as children)
const PersonInline: React.FC<{ person?: IAuthor | IEditor | IReviewer }> = (props) => {
  if (!props.person) {
    return null;
  }
  const {
    firstName = '',
    lastName = '',
    picture = '',
    credential,
    displayName,
  } = props.person.fields;
  const initials = firstName.charAt(0) + lastName.charAt(0);
  return (
    <Flex key={firstName + lastName} alignSelf="flex-start" alignItems="center">
      <Box display={['none', null, null, 'initial']}>
        <Avatar size="lg" src={picture} initials={initials} />
      </Box>
      <Box marginLeft={['0', null, null, '4']} marginBottom="0">
        <Text {...typography.authorName} marginBottom={0} mt="0">
          {displayName}
          {credential && (
            <>
              <Text as="span" {...typography.authorName}>
                ,&nbsp;
              </Text>
              <Text as="i" {...typography.authorName} fontWeight="regular" margin="0">
                {credential}
              </Text>
            </>
          )}
        </Text>
        {props.children}
      </Box>
    </Flex>
  );
};

const PersonWithBio: React.FC<{ person: IAuthor | IEditor | IReviewer; roleText: string }> = (
  props,
) => {
  const {
    firstName = '',
    lastName = '',
    picture = '',
    displayName,
    bio = '',
    website = '',
    twitterUsername = '',
    instagramUsername = '',
    linkedInUsername = '',
  } = props.person.fields;
  const initials = firstName.charAt(0) + lastName.charAt(0);
  const renderLink = (url: string, iconComponent: JSX.Element | typeof Icon) => (
    <Link
      aria-label=""
      href={url}
      target="_blank"
      analyticsName=""
      display="flex"
      alignItems="center"
      opacity="0.2"
      _hover={{ opacity: '1.0' }}
    >
      {iconComponent}
    </Link>
  );

  return (
    <>
      <SelectiveSpritesheet icons={[NewTab]} />
      <Flex key={firstName + lastName} flexDirection="column" marginBottom={4}>
        <Avatar size="lg" src={picture} initials={initials} />
        <Flex marginTop={4} flexWrap="wrap" gridColumnGap="4">
          <Box>
            <Text as="span" {...typography.authorName}>
              {displayName}
            </Text>
            <Text as="span" {...typography.authorName}>
              ,&nbsp;
            </Text>
            <Text as="i" {...typography.authorName} fontWeight="regular">
              {props.roleText}
            </Text>
          </Box>
          <Flex gridGap="4" display="inline-flex" marginBottom={2}>
            {twitterUsername &&
              renderLink(
                getSocialLink('twitter', twitterUsername),
                <NextImage
                  loader={imgixLoader}
                  width={20}
                  height={20}
                  layout="intrinsic"
                  src="twitter-icon.svg"
                />,
              )}
            {instagramUsername &&
              renderLink(
                getSocialLink('instagram', instagramUsername),
                <NextImage
                  loader={imgixLoader}
                  width={20}
                  height={20}
                  layout="intrinsic"
                  src="instagram-icon.svg"
                />,
              )}
            {linkedInUsername &&
              renderLink(
                getSocialLink('linkedin', linkedInUsername),
                <NextImage
                  loader={imgixLoader}
                  width={20}
                  height={20}
                  layout="intrinsic"
                  src="linkedin-icon.svg"
                />,
              )}
            {website && renderLink(website, <Icon name="new-tab" size={16} />)}
          </Flex>
        </Flex>
        <Text size="s1" marginTop={0}>
          {bio}
        </Text>
      </Flex>
    </>
  );
};

export { PersonInline, PersonWithBio };
